import { useMutation, useQuery } from '@apollo/client';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { UpdatePublisherReportStatusDocument } from '../../../../../../graphql/mutation/audiences/UpdatePublisherReportStatus.generated';
import { GetAudienceDocument } from '../../../../../../graphql/query/audiences/GetAudience.generated';
import { GetAudienceCategoriesReachDocument } from '../../../../../../graphql/query/audiences/GetAudienceCategoriesReach.generated';
import { GetSfAccountNamesDocument } from '../../../../../../graphql/query/audiences/GetSfAccountNames.generated';
import { isInsightRequestSupported } from '../utils/isInsightRequestSupported';
import { audienceInsightsContext, type Publisher, ReportStatusValues, TOTAL_ID } from './audienceInsights';
import { AUDIENCE_INSIGHTS_TEXTS } from './audienceInsightsTexts';
import { mapToStatistics } from './mapToStatistics';

type AudienceInsightsProviderProps = {
  children: React.ReactNode;
};

export function AudienceInsightsProvider({ children }: AudienceInsightsProviderProps) {
  const { audienceUid = '' } = useParams<{ audienceUid: string }>();
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      publisherId: TOTAL_ID,
    },
  });

  const observablePublisherId = useWatch({ control: form.control, name: 'publisherId' });

  const {
    data: { audience_by_pk: audienceData = undefined } = {},
    refetch: refetchAudience,
    loading: audienceLoading,
    error: audienceError,
  } = useQuery(GetAudienceDocument, {
    variables: { id: audienceUid },
  });

  const [updatePublisherReport] = useMutation(UpdatePublisherReportStatusDocument, {
    variables: {
      id: audienceUid,
    },
  });

  const {
    status,
    report_status: reportStatus,
    export_type: exportType,
    direct_audience_provider: directAudienceProvider,
  } = audienceData || {};
  const isGeneratingReport = reportStatus === ReportStatusValues.SF_PROCESSING;
  const isReportGenerated = reportStatus === ReportStatusValues.SF_COMPLETE;
  const isReportError = reportStatus === ReportStatusValues.SF_ERROR;
  const isGenerateButtonDisabled = isGeneratingReport || status !== 'exported';

  const displayInsightsPanel = isInsightRequestSupported(exportType, reportStatus, directAudienceProvider);
  const insightsDisabledByConfig = !displayInsightsPanel;

  const {
    data: audienceCategoriesReach,
    loading: audienceCategoriesReachLoading,
    error: audienceCategoriesReachError,
  } = useQuery(GetAudienceCategoriesReachDocument, {
    variables: {
      id: audienceUid,
    },
    skip: insightsDisabledByConfig,
  });

  const {
    loading: accountsLoading,
    error: accountsError,
    data: { sf_account_names: accounts = [] } = { sf_account_names: [] },
  } = useQuery(GetSfAccountNamesDocument, {
    variables: { id: audienceUid },
    skip: insightsDisabledByConfig,
  });

  const configDisabledText =
    reportStatus === ReportStatusValues.PROCESSING ? t(AUDIENCE_INSIGHTS_TEXTS.processingMessage) : undefined;

  const publishers: Publisher[] = useMemo(() => {
    return [...(accounts ?? []).map(account => ({ id: account ?? '', label: account ?? '' }))];
  }, [accounts]);

  const changePublisher = useCallback(
    (id: string) => {
      form.setValue('publisherId', id);
    },
    [form]
  );

  const generatePublisherReport = useCallback(() => {
    updatePublisherReport({
      variables: {
        id: audienceUid,
      },
    }).finally(() => refetchAudience());
  }, [audienceUid, updatePublisherReport, refetchAudience]);

  const statistics = mapToStatistics(audienceCategoriesReach, observablePublisherId);

  const { isDisabled, disabledMessage } = useMemo(() => {
    if (insightsDisabledByConfig) {
      return {
        isDisabled: true,
        disabledMessage: configDisabledText || t(AUDIENCE_INSIGHTS_TEXTS.otherProvidersMessage),
      };
    }

    return {
      isDisabled: false,
      disabledMessage: undefined,
    };
  }, [insightsDisabledByConfig, configDisabledText, t]);

  const value = useMemo(() => {
    const loading = audienceLoading || audienceCategoriesReachLoading;
    const error = audienceError ?? audienceCategoriesReachError;

    return {
      publisherId: observablePublisherId,
      publishers,
      isGeneratingReport,
      isReportGenerated,
      isReportError,
      isGenerateButtonDisabled,
      isDisabled,
      disabledMessage,

      statistics,

      accountsLoading,
      accountsError,

      generatePublisherReport,
      changePublisher,
      loading,
      error,
    };
  }, [
    observablePublisherId,
    publishers,
    isGenerateButtonDisabled,
    isGeneratingReport,
    isReportError,
    isReportGenerated,
    isDisabled,
    disabledMessage,
    statistics,
    accountsLoading,
    accountsError,
    changePublisher,
    generatePublisherReport,
    audienceCategoriesReachError,
    audienceCategoriesReachLoading,
    audienceError,
    audienceLoading,
  ]);

  return (
    <audienceInsightsContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </audienceInsightsContext.Provider>
  );
}
